import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import DexScreenerModal from '../components/DexScreenerModal';
import TrackerDetailModal from '../components/TrackerDetailModal';
import TierBadge from '../components/TierBadge';
import { useAutoRefresh, AutoRefreshBadge } from '../hooks/useAutoRefresh';
import { formatScanSource } from '../utils/scanSource';
import {
  formatPercent as formatTrackerPercent,
  formatTimeAgo,
  getTrackerLabel,
} from '../utils/trackerUtils';

export default function TokenDetail() {
  const { chain, address } = useParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [chartModal, setChartModal] = useState({ isOpen: false, chain: '', tokenAddress: '', tokenSymbol: '' });
  const [deleting, setDeleting] = useState(false);
  const [trackerModal, setTrackerModal] = useState({
    isOpen: false,
    tracker: null,
    tokenSymbol: '',
    currentMarketCap: null,
    recoverySnapshot: null,
  });

  const openTrackerModal = (tracker) => {
    setTrackerModal({
      isOpen: true,
      tracker,
      tokenSymbol: token?.symbol,
      currentMarketCap: token?.currentMarketCap,
      recoverySnapshot: token?.recoverySnapshot,
    });
  };

  const fetchTokenDetails = useCallback(async () => {
    try {
      const response = await api.get(`/analytics/token/${chain}/${address}`);
      setToken(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching token details:', err);
      setError(err.response?.data?.error || 'Failed to load token details');
    }
  }, [chain, address]);

  const { loading, refreshing, lastUpdated, refresh } = useAutoRefresh(fetchTokenDetails, [fetchTokenDetails]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAddress(text);
      setTimeout(() => setCopiedAddress(null), 2000);
    });
  };

  const formatMarketCap = (mc) => {
    if (!mc && mc !== 0) return 'N/A';
    if (mc >= 1000000000) return `$${(mc / 1000000000).toFixed(2)}B`;
    if (mc >= 1000000) return `$${(mc / 1000000).toFixed(2)}M`;
    if (mc >= 1000) return `$${(mc / 1000).toFixed(2)}K`;
    return `$${mc.toFixed(0)}`;
  };

  const formatVolume = (vol) => {
    if (!vol && vol !== 0) return '$0';
    if (vol >= 1000000) return `$${(vol / 1000000).toFixed(2)}M`;
    if (vol >= 1000) return `$${(vol / 1000).toFixed(2)}K`;
    return `$${vol.toFixed(0)}`;
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return 'N/A';
    const prefix = value >= 0 ? '+' : '';
    if (Math.abs(value) >= 10000) {
      return `${prefix}${(value / 1000).toFixed(1)}K%`;
    }
    return `${prefix}${value.toFixed(2)}%`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return `${Math.floor(diffDays / 7)}w ago`;
  };

  const getChainEmoji = (chain) => {
    const emojis = {
      solana: '◎',
      base: '🔵',
      ethereum: '⟠',
      bsc: '🟡'
    };
    return emojis[chain] || '⛓️';
  };

  const generateTradeUrl = (chain, address) => {
    if (chain === 'solana' || chain === 'bsc') {
      return `https://axiom.trade/t/${address}`;
    }
    if (chain === 'base') {
      return `https://gmgn.ai/base/token/${address}`;
    }
    if (chain === 'ethereum') {
      return `https://gmgn.ai/eth/token/${address}`;
    }
    return null;
  };

  const getTradeButtonLabel = (chain) => {
    if (chain === 'solana' || chain === 'bsc') return 'Axiom';
    if (chain === 'base' || chain === 'ethereum') return 'GMGN';
    return 'Trade';
  };

  const getFomoUrl = (chain, address) => {
    const slug = { solana: 'solana', ethereum: 'ethereum', base: 'base', bsc: 'bnb' }[chain];
    return slug && address ? `https://fomo.family/tokens/${slug}/${address}` : null;
  };

  const getDexScreenerUrl = (chain, address) => {
    return `https://dexscreener.com/${chain}/${address}`;
  };

  const handleErase = async () => {
    const symbol = token?.symbol || address;
    if (!window.confirm(
      `Remove "${symbol}" from tracking?\n\nThis deletes the token from all chats, milestones, and recovery records. This cannot be undone.`
    )) {
      return;
    }
    setDeleting(true);
    try {
      await api.delete('/tokens/by-address', { params: { chain, address } });
      navigate('/tokens');
    } catch (err) {
      console.error('Error deleting token:', err);
      setError(err.response?.data?.error || 'Failed to remove token from tracking');
    } finally {
      setDeleting(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'milestones', name: 'Milestones', icon: '🎯' },
    { id: 'recovery', name: 'Recovery', icon: '📈' },
    { id: 'trackers', name: 'Trackers', icon: '👥' },
  ];

  if (loading && !token) {
    return (
      <div className="p-6">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">Loading token details...</div>
      </div>
    );
  }

  if (error && !token) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 text-blue-600 hover:text-blue-800 flex items-center gap-1"
        >
          ← Back
        </button>
        <div className="bg-red-50 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 ${refreshing ? 'opacity-90' : ''} transition-opacity`}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            ← Back
          </button>
          <div className="flex items-center gap-3">
            <AutoRefreshBadge refreshing={refreshing} lastUpdated={lastUpdated} />
            <button
              type="button"
              onClick={refresh}
              disabled={refreshing}
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            {/* Token Info */}
            <div className="flex items-start gap-4">
              <div className="text-4xl">{getChainEmoji(token?.chain)}</div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  {token?.symbol}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400 capitalize">({token?.chain})</span>
                </h1>
                <p className="text-gray-600">{token?.name}</p>
                <div className="flex items-center gap-2 mt-2">
                  <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono">
                    {token?.address}
                  </code>
                  <button
                    onClick={() => copyToClipboard(token?.address)}
                    className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                  >
                    {copiedAddress === token?.address ? '✓ Copied' : '📋'}
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {getFomoUrl(token?.chain, token?.address) && (
                <a
                  href={getFomoUrl(token?.chain, token?.address)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium"
                >
                  Fomo
                </a>
              )}
              {generateTradeUrl(token?.chain, token?.address) && (
                <a
                  href={generateTradeUrl(token?.chain, token?.address)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 font-medium"
                >
                  Trade on {getTradeButtonLabel(token?.chain)}
                </a>
              )}
              <button
                onClick={() => setChartModal({
                  isOpen: true,
                  chain: token?.chain,
                  tokenAddress: token?.address,
                  tokenSymbol: token?.symbol
                })}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 font-medium"
              >
                📊 Chart
              </button>
              <a
                href={getDexScreenerUrl(token?.chain, token?.address)}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium"
              >
                DexScreener
              </a>
              <button
                type="button"
                onClick={handleErase}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
              >
                {deleting ? 'Removing…' : '🗑 Remove'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Initial MC</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">{formatMarketCap(token?.initialMarketCap)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current MC</div>
          <div className="text-lg font-bold text-blue-600">{formatMarketCap(token?.currentMarketCap)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">ATH MC</div>
          <div className="text-lg font-bold text-green-600">{formatMarketCap(token?.allTimeHigh)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">ATL MC</div>
          <div className="text-lg font-bold text-red-600">{formatMarketCap(token?.allTimeLow)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Gain from IMC</div>
          <div className={`text-lg font-bold ${token?.gainFromInitial >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatPercent(token?.gainFromInitial)}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">From ATH</div>
          <div className={`text-lg font-bold ${token?.fromATH >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatPercent(token?.fromATH)}
          </div>
        </div>
      </div>

      {/* Volume Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">4h Volume</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">{formatVolume(token?.volume?.h4)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">12h Volume</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">{formatVolume(token?.volume?.h12)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">24h Volume</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">{formatVolume(token?.volume?.h24)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Liquidity</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">{formatVolume(token?.liquidity)}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap py-3 px-4 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Token Details */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Token Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-500">Added</span>
                    <span className="font-medium">{formatDate(token?.addedAt)} ({formatTimeAgo(token?.addedAt)})</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-500">Added By</span>
                    <span className="font-medium">{token?.addedBy || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-500">Scanned In</span>
                    <span className="font-medium">{token?.scanSource || formatScanSource(token).label}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-500">Last Updated</span>
                    <span className="font-medium">{formatDate(token?.lastUpdatedAt)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-500">Highest Milestone</span>
                    <span className="font-bold text-purple-600">{token?.highestMultiplier ? `${token.highestMultiplier}x` : 'None'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-500">Total Milestones</span>
                    <span className="font-medium">{token?.totalMilestones || 0}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-500">Recovery Events</span>
                    <span className="font-medium">{token?.totalRecoveries || 0}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-500">Active Trackers</span>
                    <span className="font-medium">{token?.trackerCount || 0}</span>
                  </div>
                </div>
              </div>

              {/* Price History */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Price History</h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-500">Initial Price</span>
                    <span className="font-medium">${token?.initialPrice?.toFixed(8) || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-500">Current Price</span>
                    <span className="font-medium text-blue-600">${token?.currentPrice?.toFixed(8) || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-500">ATH Price</span>
                    <span className="font-medium text-green-600">${token?.athPrice?.toFixed(8) || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-500">ATH Date</span>
                    <span className="font-medium">{formatDate(token?.allTimeHighDate)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-500">ATL Price</span>
                    <span className="font-medium text-red-600">${token?.atlPrice?.toFixed(8) || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-500">ATL Date</span>
                    <span className="font-medium">{formatDate(token?.allTimeLowDate)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Milestones Tab */}
        {activeTab === 'milestones' && (
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-2">Milestone History</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Global ATH milestones below. Per-group scan milestones are tied to each tracker&apos;s initial cap.
            </p>

            {token?.trackers?.length > 0 && (
              <div className="mb-8">
                <h4 className="text-sm font-semibold uppercase text-gray-500 dark:text-gray-400 mb-3">
                  Per-tracker milestone alerts
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {token.trackers.map((tracker) => (
                    <button
                      key={tracker.chatId}
                      type="button"
                      onClick={() => openTrackerModal(tracker)}
                      className="text-left rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-purple-300 dark:hover:border-purple-700 hover:bg-purple-50/40 dark:hover:bg-purple-900/10 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{getTrackerLabel(tracker)}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            IMC {formatMarketCap(tracker.initialMarketCap)} · {formatTimeAgo(tracker.addedAt)}
                          </div>
                        </div>
                        <TierBadge tier={tracker.tier} />
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(tracker.milestonesReached || []).length > 0 ? (
                          [...tracker.milestonesReached].sort((a, b) => a - b).map((m) => (
                            <span key={m} className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
                              {m}x
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">No milestone alerts yet</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <h4 className="text-sm font-semibold uppercase text-gray-500 dark:text-gray-400 mb-3">
              Global price milestones
            </h4>
            {token?.milestones?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {token.milestones
                  .sort((a, b) => b.multiplier - a.multiplier)
                  .map((milestone, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border">
                    <div className="flex justify-between items-start mb-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-purple-100 text-purple-800">
                        {milestone.multiplier}x
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{formatTimeAgo(milestone.achievedAt)}</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">At MC:</span>
                        <span className="font-medium text-green-600">{formatMarketCap(milestone.currentMarketCap)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Gain:</span>
                        <span className="font-bold text-green-600">+{milestone.percentageGain?.toFixed(2)}%</span>
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500">{formatDate(milestone.achievedAt)}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">No milestones achieved yet</div>
            )}
          </div>
        )}

        {/* Recovery Tab */}
        {activeTab === 'recovery' && (
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-2">Recovery</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Bottom and bounce are shared per token. Alerts fire per group based on that group&apos;s scan time and tier.
            </p>

            {token?.recoverySnapshot && (
              <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-3">
                  <div className="text-xs text-gray-500">Bottom</div>
                  <div className="font-bold text-red-600">{formatMarketCap(token.recoverySnapshot.bottomMarketCap)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-3">
                  <div className="text-xs text-gray-500">Recovery</div>
                  <div className="font-bold text-green-600">{formatTrackerPercent(token.recoverySnapshot.recoveryPercentage)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-3">
                  <div className="text-xs text-gray-500">From ATH</div>
                  <div className="font-bold">{token.recoverySnapshot.recoveryFromAthPercentage?.toFixed?.(1)}%</div>
                </div>
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-3">
                  <div className="text-xs text-gray-500">Drawdown</div>
                  <div className="font-bold text-red-600">{token.recoverySnapshot.drawdownPercentage?.toFixed?.(1)}%</div>
                </div>
              </div>
            )}

            {token?.trackers?.length > 0 && (
              <div className="mb-8">
                <h4 className="text-sm font-semibold uppercase text-gray-500 dark:text-gray-400 mb-3">
                  Per-tracker recovery alerts
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {token.trackers.map((tracker) => (
                    <button
                      key={tracker.chatId}
                      type="button"
                      onClick={() => openTrackerModal(tracker)}
                      className="text-left rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{getTrackerLabel(tracker)}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            IMC {formatMarketCap(tracker.initialMarketCap)} · gain {formatTrackerPercent(tracker.gainFromInitial)}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <TierBadge tier={tracker.tier} />
                          {tracker.isRecovering && (
                            <span className="text-xs font-semibold text-green-600">Recovering</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(tracker.recoveryMilestonesReached || []).length > 0 ? (
                          [...tracker.recoveryMilestonesReached].sort((a, b) => a - b).map((m) => (
                            <span key={m} className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                              +{m}%
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">No recovery milestones yet</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <h4 className="text-sm font-semibold uppercase text-gray-500 dark:text-gray-400 mb-3">
              Recovery event log
            </h4>
            {token?.recoveries?.length > 0 ? (
              <div className="space-y-4">
                {token.recoveries.map((recovery, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border">
                    <div className="flex justify-between items-start mb-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        recovery.type === 'detected' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800 dark:text-green-300'
                      }`}>
                        {recovery.type === 'detected' ? '🔍 Recovery Detected' : '📈 Recovery Alert'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{formatTimeAgo(recovery.timestamp)}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-gray-500">Bottom</div>
                        <div className="font-medium">{formatMarketCap(recovery.bottomMarketCap)}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Current</div>
                        <div className="font-medium text-green-600">{formatMarketCap(recovery.currentMarketCap)}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Drawdown</div>
                        <div className="font-medium text-red-600">{recovery.drawdownPercent?.toFixed(1)}%</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Recovery</div>
                        <div className="font-medium text-green-600">+{recovery.recoveryPercent?.toFixed(1)}%</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">No recovery events recorded</div>
            )}
          </div>
        )}

        {/* Trackers Tab */}
        {activeTab === 'trackers' && (
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-2">Active Trackers</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Click a tracker for its initial cap, milestone tags, and recovery alert state.
            </p>
            {token?.trackers?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Group</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tier</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Caller</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Added</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">IMC</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Milestones</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Recovery</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {token.trackers.map((tracker) => (
                      <tr
                        key={tracker.chatId}
                        className="hover:bg-blue-50/60 dark:hover:bg-blue-900/10 cursor-pointer"
                        onClick={() => openTrackerModal(tracker)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {tracker.scanSource || getTrackerLabel(tracker)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <TierBadge tier={tracker.tier} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {tracker.addedBy || tracker.sharedBy || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(tracker.addedAt)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                          {formatMarketCap(tracker.initialMarketCap)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {(tracker.milestonesReached || []).slice(0, 3).map((m) => (
                            <span key={m} className="mr-1 px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-800">{m}x</span>
                          ))}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {(tracker.recoveryMilestonesReached || []).slice(0, 3).map((m) => (
                            <span key={m} className="mr-1 px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800">+{m}%</span>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">No active trackers</div>
            )}
          </div>
        )}
      </div>

      {/* DexScreener Chart Modal */}
      <DexScreenerModal
        isOpen={chartModal.isOpen}
        onClose={() => setChartModal({ ...chartModal, isOpen: false })}
        chain={chartModal.chain}
        tokenAddress={chartModal.tokenAddress}
        tokenSymbol={chartModal.tokenSymbol}
      />

      <TrackerDetailModal
        isOpen={trackerModal.isOpen}
        onClose={() => setTrackerModal({ ...trackerModal, isOpen: false })}
        tracker={trackerModal.tracker}
        tokenSymbol={trackerModal.tokenSymbol}
        currentMarketCap={trackerModal.currentMarketCap}
        recoverySnapshot={trackerModal.recoverySnapshot}
      />
    </div>
  );
}
